import { useMemo } from 'react';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import { Modality } from '~/domain/dicom/modality';
import {
    APIType,
    ContouringCustomizationFile,
    UserCustomization,
} from '~/backend/types';
import type { IdentifiedRoi } from '~/domain/comparison/roiCounterpart';
import { isAuthenticationEnabled } from '~/lib/auth';
import type { ContourModelChoice } from '~/state/atoms/adaptiveReplanningAtoms';
import AppConfig from '~/utils/appconfig';
import { transformModelRoiNames } from '~/utils/transforms/modelRoiTransforms';
import { useModelCustomizations } from '../useBackend';
import { bestRanked, rankByOverlap, TypedRoi } from './roiOverlapMatch';
import { useComparisonContourModels } from './useComparisonContourModel';

const DEFAULT_CUSTOMIZATION_NAME = 'default';
// The catalog's body parts for models that run several regions' networks together.
const COMBINED_BODY_PARTS: ReadonlySet<string> = new Set([
    'WholeBody',
    'Combination',
]);

interface BestContourModel {
    // False until the catalog and the source structures have both loaded.
    readonly settled: boolean;
    readonly choice?: ContourModelChoice;
    // Every model and customization, best match first.
    readonly ranking: readonly ContourModelChoice[];
    // Why no match can be made: the models' structures need a signed-in user, or could not be loaded.
    readonly unavailable?: 'signedOut' | 'failed';
}

interface Candidate extends ContourModelChoice {
    readonly rois: readonly IdentifiedRoi[];
    readonly combined: boolean;
}

export function contourModelCandidates(
    modelNames: readonly string[],
    customizations: UserCustomization<ContouringCustomizationFile>[]
): Candidate[] {
    return modelNames.flatMap((modelName) => {
        const entry = customizations.find(
            (model) => model.model_name === modelName
        );
        const names = (entry?.customizations ?? [])
            .map((customization) => customization.customization_name)
            // The default first, so it wins a tie with a customization of its own model.
            .sort(
                (a, b) =>
                    Number(b === DEFAULT_CUSTOMIZATION_NAME) -
                    Number(a === DEFAULT_CUSTOMIZATION_NAME)
            );
        const combined = COMBINED_BODY_PARTS.has(entry?.body_part ?? '');
        return names.map((name) => ({
            modelName,
            combined,
            customizationName:
                name === DEFAULT_CUSTOMIZATION_NAME ? undefined : name,
            rois: transformModelRoiNames(customizations, modelName, name).rois,
        }));
    });
}

export function useBestMatchingContourModel(
    sourceRois: readonly TypedRoi[],
    modality: Modality = Modality.CT
): BestContourModel {
    const models = useComparisonContourModels(modality);
    const { data: customizations, isError } = useModelCustomizations(
        APIType.CONTOURING
    );
    const { isAuthenticated } = useAuthSession();
    const signedOut =
        isAuthenticationEnabled() &&
        !AppConfig.isConnectorMode &&
        !isAuthenticated;

    return useMemo(() => {
        if (signedOut || isError) {
            return {
                settled: false,
                ranking: [],
                unavailable: signedOut ? 'signedOut' : 'failed',
            };
        }
        if (models.length === 0 || !customizations || sourceRois.length === 0) {
            return { settled: false, ranking: [] };
        }
        const ranked = rankByOverlap(
            contourModelCandidates(
                models.map((model) => model.name),
                customizations as UserCustomization<ContouringCustomizationFile>[]
            ),
            sourceRois
        );
        const choiceOf = (
            candidate: ContourModelChoice
        ): ContourModelChoice => ({
            modelName: candidate.modelName,
            customizationName: candidate.customizationName,
        });
        const best = bestRanked(ranked);
        return {
            settled: true,
            choice: best && choiceOf(best),
            ranking: ranked.map(({ candidate }) => choiceOf(candidate)),
        };
    }, [models, customizations, sourceRois, signedOut, isError]);
}
